import { useTranslation } from "@/hooks/useTranslation";
import { formatCurrency } from "@/lib/format-amount";
import { cn } from "@/lib/utils";

type LicenseChangeSummaryProps = {
  /** Seats the team holds today on this frequency. */
  current: number;
  /** Seats the selector is set to. */
  target: number;
  /** Recurring price of one seat, excl. tax, in major units. */
  unitAmount: number;
  /** The team's percentage discount, applied to the amount shown. */
  percentOff?: number | null;
  currency: string;
  /** "/mois" or "/an". */
  perPeriod: string;
};

/**
 * Says what the selector will change before it is confirmed (#742): the
 * dialog's counter holds the new total, which read as "how many to add" — a
 * customer typed 1 to add one seat. The current stock, the difference and its
 * recurring amount make the total unambiguous.
 */
export const LicenseChangeSummary = ({
  current,
  target,
  unitAmount,
  percentOff,
  currency,
  perPeriod,
}: LicenseChangeSummaryProps) => {
  const { t, i18n } = useTranslation();
  const prefix = "team.licenseManagement.dialog";
  const delta = target - current;

  const deltaLabel =
    delta > 0
      ? t(`${prefix}.deltaAdd`, { count: delta })
      : delta < 0
        ? t(`${prefix}.deltaRemove`, { count: -delta })
        : t(`${prefix}.noChange`);

  const amount =
    delta === 0
      ? null
      : t(`${prefix}.${delta > 0 ? "deltaAmountAdd" : "deltaAmountRemove"}`, {
          amount: formatCurrency(
            Math.abs(delta) * unitAmount * (1 - (percentOff ?? 0) / 100),
            currency,
            i18n.language
          ),
          period: perPeriod,
        });

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">
        {t(`${prefix}.currentStock`, { count: current })}
      </span>
      <span
        className={cn(
          "font-medium",
          delta > 0 && "text-green-600 dark:text-green-400",
          delta < 0 && "text-destructive"
        )}
      >
        {deltaLabel}
        {amount && ` · ${amount}`}
      </span>
    </div>
  );
};
